import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Printer } from "lucide-react";

const CRIT_COLOR = { Critical: "bg-red-100 text-red-800", High: "bg-amber-100 text-amber-800", Medium: "bg-slate-100 text-slate-700", Low: "bg-slate-50 text-slate-500" };

export default function Assets() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [criticality, setCriticality] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ asset_group: "", name: "", location: "", criticality: "Medium", description: "" });
  const nav = useNavigate();

  const load = useCallback(async () => {
    const p = {};
    if (q) p.q = q;
    if (group !== "all") p.group = group;
    if (criticality !== "all") p.criticality = criticality;
    const { data } = await api.get("/assets", { params: p });
    setRows(data);
  }, [q, group, criticality]);

  useEffect(() => { api.get("/assets/groups").then((r) => setGroups(r.data)); }, []);
  useEffect(() => { load();  }, [load]);

  const save = async () => {
    try {
      await api.post("/assets", form);
      toast.success("Asset created");
      setOpen(false);
      setForm({ asset_group: "", name: "", location: "", criticality: "Medium", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create asset");
    }
  };

  return (
    <div className="space-y-4" data-testid="assets-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Register</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Assets</h1>
          <p className="text-sm text-slate-600 mt-1">{rows.length} asset{rows.length === 1 ? "" : "s"} in register</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={()=>{
              const p = new URLSearchParams();
              if (group!=="all") p.set("group", group);
              if (criticality!=="all") p.set("criticality", criticality);
              const qs = p.toString();
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/qr-sheet.pdf${qs?`?${qs}`:""}`, "_blank");
            }}
            data-testid="qr-sheet-btn"
          ><Printer className="w-4 h-4 mr-1"/>Print QR sheet</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-1"/> New asset</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create asset</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Asset group *</Label><Input value={form.asset_group} onChange={(e)=>setForm({...form, asset_group:e.target.value})} data-testid="asset-form-group"/></div>
              <div><Label>Name *</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="asset-form-name"/></div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} data-testid="asset-form-location"/></div>
              <div><Label>Criticality</Label>
                <Select value={form.criticality} onValueChange={(v)=>setForm({...form, criticality:v})}>
                  <SelectTrigger data-testid="asset-form-criticality"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Critical">Critical</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="asset-form-description"/></div>
            </div>
            <DialogFooter><Button onClick={save} disabled={!form.asset_group || !form.name} data-testid="asset-form-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <Input placeholder="Search name, group, location…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="assets-search"/>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger data-testid="assets-filter-group"><SelectValue placeholder="Group"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g)=>(<SelectItem key={g.group} value={g.group}>{g.group} ({g.count})</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={criticality} onValueChange={setCriticality}>
              <SelectTrigger data-testid="assets-filter-crit"><SelectValue placeholder="Criticality"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All criticality</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={()=>{setQ("");setGroup("all");setCriticality("all");}} data-testid="assets-clear-filters">Clear filters</Button>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <Table className="dense-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Criticality</TableHead>
                  <TableHead>Next service</TableHead>
                  <TableHead>Responsible</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer hover:bg-slate-100" onClick={()=>nav(`/assets/${r.id}`)} data-testid={`asset-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-900">{r.name}</TableCell>
                    <TableCell className="text-slate-600">{r.asset_group}</TableCell>
                    <TableCell className="text-slate-600">{r.location || "—"}</TableCell>
                    <TableCell><Badge className={`${CRIT_COLOR[r.criticality] || "bg-slate-100 text-slate-700"} border-0`}>{r.criticality}</Badge></TableCell>
                    <TableCell className="text-slate-600 text-xs font-mono">{r.next_service_date ? new Date(r.next_service_date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{r.responsible_party || "—"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No assets match your filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
